import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  CardSubtitle,
} from '../Card';
import { renderIcon } from '../Dashboard/iconUtils';

import { useTranslation } from 'react-i18next';

export interface CategoryItem {
  id: string;
  name: string;
  count: number;
  description?: string;
  icon?: {
    name: string; // Lucide icon name
    color: string; // hex color for icon
    size?: number; // icon size in pixels (default: 24)
  };
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
}

export interface CategoryCardProps {
  /** The title of the category card */
  title?: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Array of category items to display */
  items?: CategoryItem[];
  /** Whether the card is loading */
  isLoading?: boolean;
  /** Optional custom className for styling */
  className?: string;
  /** Optional click handler for items */
  onItemClick?: (item: CategoryItem) => void;
  /** Layout variant - grid or list */
  layout?: 'grid' | 'list';
  /** Number of columns for grid layout (default: 2) */
  columns?: number;
  /** Whether to show item descriptions */
  showDescriptions?: boolean;
  /** Whether items are clickable */
  clickable?: boolean;
  /** Optional title icon */
  titleIcon?: string;
  /** Whether data is streaming from LLM */
  isStreaming?: boolean;
}

const renderCategoryIcon = (icon: CategoryItem['icon']) => {
  if (!icon?.name) {
    return null;
  }

  const size = icon.size || 24;
  const color = icon.color || '#6b7280';

  const iconElement = renderIcon({
    name: icon.name,
    size,
    color,
  });

  if (!iconElement) {
    return null;
  }

  return (
    <div
      className="flex items-center justify-center rounded-md"
      style={{
        backgroundColor: `${color}25`,
        width: `${size + 20}px`,
        height: `${size + 20}px`,
      }}
    >
      {iconElement}
    </div>
  );
};

const CategoryCard: React.FC<CategoryCardProps> = ({
  title,
  titleIcon,
  subtitle,
  items = [],
  isLoading = false,
  className,
  onItemClick,
  layout = 'grid',
  columns = 1,
  showDescriptions = false,
  clickable = false,
}) => {
  const { t } = useTranslation('pleri/dashboard');
  // Show loading state if explicitly loading or if no data is provided
  if (isLoading || (!title && !subtitle && items.length === 0)) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="w-full animate-pulse">
            <div className="mb-4 h-4 w-24 rounded bg-gray-200 dark:bg-gray-700"></div>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="flex-1">
                  <div className="mb-1 h-4 w-16 rounded bg-gray-200 dark:bg-gray-700"></div>
                  <div className="h-3 w-20 rounded bg-gray-200 dark:bg-gray-700"></div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="flex-1">
                  <div className="mb-1 h-4 w-16 rounded bg-gray-200 dark:bg-gray-700"></div>
                  <div className="h-3 w-20 rounded bg-gray-200 dark:bg-gray-700"></div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  const handleItemClick = (item: CategoryItem) => {
    if (clickable && onItemClick) {
      onItemClick(item);
    }
  };

  const renderGridLayout = () => (
    <div
      className="grid gap-3"
      style={{
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
      }}
    >
      {items.map((item) => (
        <div
          key={item.id}
          className={`group flex items-center gap-3 ${
            clickable ? 'cursor-pointer' : ''
          }`}
          onClick={() => handleItemClick(item)}
        >
          {renderCategoryIcon(item.icon)}
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2">
              <span className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                {item.count?.toLocaleString()}
              </span>
            </div>
            <div className="text-sm font-normal text-gray-600 dark:text-gray-400">
              {item.name}
            </div>
            {showDescriptions && item.description && (
              <p className="mt-0.5 text-xs text-gray-500">{item.description}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderListLayout = () => (
    <div className="space-y-2">
      {items.map((item) => (
        <div
          key={item.id}
          className={`group flex items-center gap-3 ${
            clickable ? 'cursor-pointer' : ''
          }`}
          onClick={() => handleItemClick(item)}
        >
          {renderCategoryIcon(item.icon)}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-normal text-gray-600 dark:text-gray-400">
              {item.name}
            </div>
            {showDescriptions && item.description && (
              <p className="mt-0.5 text-xs text-gray-500">{item.description}</p>
            )}
          </div>
          <span className="flex-shrink-0 text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">
            {item.count?.toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <Card className={className}>
      {(title || subtitle) && (
        <CardHeader className="pb-2">
          {title && (
            <CardTitle className="flex items-center gap-2">
              {titleIcon && (
                <span className="text-gray-500 dark:text-gray-400">
                  {renderIcon({ name: titleIcon, size: 20 })}
                </span>
              )}
              {title || t('categories')}
            </CardTitle>
          )}
          {subtitle && <CardSubtitle>{subtitle}</CardSubtitle>}
        </CardHeader>
      )}
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: Math.min(items.length || 4, 4) }).map(
              (_, index) => (
                <div key={index} className="animate-pulse">
                  <div className="flex items-center gap-3">
                    <div className="h-5 w-5 rounded bg-gray-200 dark:bg-gray-700"></div>
                    <div className="flex-1 space-y-1">
                      <div className="h-4 w-12 rounded bg-gray-200 dark:bg-gray-700"></div>
                      <div className="h-3 w-24 rounded bg-gray-200 dark:bg-gray-700"></div>
                    </div>
                  </div>
                </div>
              ),
            )}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-4 text-center">
            <div className="mb-1 flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
              <svg
                className="h-3 w-3 text-gray-400 dark:text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
            </div>
            <h3 className="text-xs font-medium text-gray-900 dark:text-gray-100">
              {t('noCategoriesFound')}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('thereAreNoItemsToDisplay')}
            </p>
          </div>
        ) : layout === 'grid' ? (
          renderGridLayout()
        ) : (
          renderListLayout()
        )}
      </CardContent>
    </Card>
  );
};

export default CategoryCard;
